import { FinsterTask, TaskRun } from "../finsterApi";
import type { Citation } from "../citations";
import { getBrandContext, type ToolExecutionPolicy } from "./ai/types";

const COMPLETED_RUN_STATES = new Set(["COMPLETE", "COMPLETED"]);

export type TaskRunResolutionMode = "explicit" | "auto_latest_completed" | "auto_latest" | "none";
export type DeckGroundingMode = "run_output" | "metadata_only" | "none";

export interface ResolvedTaskRun {
  run: TaskRun | null;
  mode: TaskRunResolutionMode;
  reason: string;
}

function parseTimestampMs(timestamp?: string): number {
  if (!timestamp) {
    return Number.NEGATIVE_INFINITY;
  }
  const parsed = Date.parse(timestamp);
  return Number.isFinite(parsed) ? parsed : Number.NEGATIVE_INFINITY;
}

function normalizeState(state?: string): string {
  return (state || "").trim().toUpperCase();
}

function compareRunsByRecency(a: TaskRun, b: TaskRun): number {
  const aTime = parseTimestampMs(a.taskRunTimestamp);
  const bTime = parseTimestampMs(b.taskRunTimestamp);
  if (aTime !== bTime) {
    return bTime - aTime;
  }
  return b.taskRunId.localeCompare(a.taskRunId);
}

export function sortTaskRunsByRecency(runs: TaskRun[]): TaskRun[] {
  return [...runs].sort(compareRunsByRecency);
}

export function resolveTaskRun(
  task: FinsterTask | null | undefined,
  selectedTaskRunId?: string | null
): ResolvedTaskRun {
  if (!task) {
    return { run: null, mode: "none", reason: "task_not_selected" };
  }

  const taskRuns = sortTaskRunsByRecency(task.taskRuns || []);
  if (taskRuns.length === 0) {
    return { run: null, mode: "none", reason: "task_has_no_runs" };
  }

  if (selectedTaskRunId) {
    const selectedRun = taskRuns.find((run) => run.taskRunId === selectedTaskRunId);
    if (selectedRun) {
      return { run: selectedRun, mode: "explicit", reason: "selected_run" };
    }
  }

  const latestCompletedRun = taskRuns.find((run) =>
    COMPLETED_RUN_STATES.has(normalizeState(run.taskRunState))
  );
  if (latestCompletedRun) {
    return {
      run: latestCompletedRun,
      mode: "auto_latest_completed",
      reason: selectedTaskRunId ? "selected_run_not_found" : "latest_completed",
    };
  }

  return {
    run: taskRuns[0],
    mode: "auto_latest",
    reason: selectedTaskRunId ? "selected_run_not_found" : "latest_run",
  };
}

function safeStringify(value: unknown): string {
  try {
    return JSON.stringify(value, null, 2);
  } catch {
    return String(value);
  }
}

/**
 * Pull the API-side `citations[]` array out of a task run output payload
 * (the same shape `finster_query` returns to the chat flow). Tolerates
 * missing / non-array values by returning an empty list, and skips
 * non-object entries so a malformed envelope can't poison the deck-wide
 * pool. The returned envelopes are forwarded as-is to `mergeIntoPool`
 * during deck creation so the source cards rendered by
 * `SlideCitationsView` resolve to the same `Citation` records the chat
 * flow uses.
 */
export function extractTaskOutputCitations(data: unknown): Citation[] {
  if (!data || typeof data !== "object") return [];
  const citations = (data as { citations?: unknown }).citations;
  if (!Array.isArray(citations)) return [];
  return citations.filter(
    (citation): citation is Citation => !!citation && typeof citation === "object"
  );
}

export function extractTaskOutputText(data: unknown): string {
  if (data === null || data === undefined) {
    return "";
  }

  if (typeof data === "string") {
    return data;
  }

  if (typeof data === "object") {
    const record = data as Record<string, unknown>;
    const prioritizedFields = ["output", "answer", "content"];
    for (const field of prioritizedFields) {
      const value = record[field];
      if (typeof value === "string") {
        return value;
      }
      if (value !== undefined && value !== null) {
        return safeStringify(value);
      }
    }
    return safeStringify(data);
  }

  return String(data);
}

export interface TruncateGroundingOptions {
  maxChars?: number;
  maxLines?: number;
  suffix?: string;
}

export function truncateGrounding(text: string, options: TruncateGroundingOptions = {}): string {
  const maxChars = options.maxChars ?? 4000;
  const maxLines = options.maxLines ?? 120;
  const suffix = options.suffix ?? "\n\n[...truncated...]";

  let trimmed = text.trim();
  if (!trimmed) {
    return "";
  }

  const lines = trimmed.split("\n");
  if (lines.length > maxLines) {
    trimmed = `${lines.slice(0, maxLines).join("\n")}${suffix}`;
  }

  if (trimmed.length > maxChars) {
    const cutoff = Math.max(0, maxChars - suffix.length);
    trimmed = `${trimmed.slice(0, cutoff)}${suffix}`;
  }

  return trimmed;
}

export interface GroundingContextInput {
  taskName: string;
  taskId: string;
  taskTopic?: string;
  selectedRun?: TaskRun | null;
  outputText?: string;
  outputFetchError?: string | null;
  toolPolicy?: ToolExecutionPolicy;
}

export interface GroundingContextResult {
  mode: Exclude<DeckGroundingMode, "none">;
  plannerContext: string;
  slideContext: string;
  message: string;
}

export function buildGroundingContexts(input: GroundingContextInput): GroundingContextResult {
  const { taskName, taskId, taskTopic, selectedRun, outputText = "", outputFetchError, toolPolicy } =
    input;
  const brand = getBrandContext(toolPolicy);

  if (outputText.trim()) {
    const fullOutput = outputText.trim();
    const plannerContextLines: Array<string | null> = [
      `[GROUNDING CONTEXT FOR PLANNER FROM ${brand.taskContextHeaderBrand} TASK RUN]`,
      `Task: ${taskName}`,
      `Task ID: ${taskId}`,
      selectedRun ? `Run ID: ${selectedRun.taskRunId}` : null,
      selectedRun?.taskRunName ? `Run name: ${selectedRun.taskRunName}` : null,
      selectedRun?.taskRunTimestamp ? `Run timestamp: ${selectedRun.taskRunTimestamp}` : null,
      "",
      fullOutput,
      `[END GROUNDING CONTEXT FOR PLANNER FROM ${brand.taskContextHeaderBrand} TASK RUN]`,
    ];
    const plannerContext = plannerContextLines
      .filter((line): line is string => line !== null)
      .join("\n");

    return {
      mode: "run_output",
      plannerContext,
      slideContext: fullOutput,
      message: selectedRun
        ? `Grounded on selected run output: ${selectedRun.taskRunName || selectedRun.taskRunId}`
        : `Grounded on ${brand.taskRunOutputLabel}.`,
    };
  }

  const metadataLines = [
    `[GROUNDING CONTEXT FOR PLANNER (TASK METADATA ONLY)]`,
    `Task: ${taskName}`,
    `Task ID: ${taskId}`,
    taskTopic ? `Task topic: ${taskTopic}` : "",
    selectedRun ? `Selected run ID: ${selectedRun.taskRunId}` : "",
    selectedRun?.taskRunName ? `Selected run name: ${selectedRun.taskRunName}` : "",
    selectedRun?.taskRunTimestamp ? `Selected run timestamp: ${selectedRun.taskRunTimestamp}` : "",
    outputFetchError ? `Run output fetch error: ${outputFetchError}` : "",
    `[END GROUNDING CONTEXT FOR PLANNER (TASK METADATA ONLY)]`,
  ].filter(Boolean);

  return {
    mode: "metadata_only",
    plannerContext: metadataLines.join("\n"),
    slideContext: truncateGrounding(metadataLines.join("\n"), {
      maxChars: 1500,
      maxLines: 80,
    }),
    message: outputFetchError
      ? "Task output could not be loaded; using metadata-only fallback."
      : "Task output was empty; using metadata-only fallback.",
  };
}
